import { useTranslation } from "react-i18next";
import type { ListedConnector } from "../../../api/connectorsApi";
import EmptyState from "../../../ui/feedback/EmptyState";
import Chip from "../../../ui/feedback/Chip";
import type { TFunction } from "i18next";
import CommandFold from "../CommandFold";
import ConnectorCommandLine, { type ConnectorCommand } from "../ConnectorCommandLine";
import OpenOnPc from "../pcPage/OpenOnPc";
import { providerCardsOf, type ProviderCard, type ProviderOnPc, type ProviderState } from "./providersOfConnectors";

const STATE_TONES: Readonly<Record<ProviderState, "success" | "warning" | "danger">> = {
  ready: "success",
  needs_sign_in: "warning",
  needs_key: "warning",
  not_installed: "warning",
  not_working: "danger",
  offline: "warning",
};

/**
 * The agents and API routes on the person's PCs, one condensed card per provider (F49, as
 * T3 Code shows them): whether it works, and where to connect it. Connecting happens on the
 * PC's own page, opened there from here (F85), never by editing connector.toml (F47: no
 * console); the command stays the backup for people who use a terminal.
 */
export default function ProviderCards({ connectors }: { connectors: readonly ListedConnector[] }) {
  const { t } = useTranslation();
  const cards = providerCardsOf(connectors);
  if (cards.length === 0) return <EmptyState>{t("connectors.providers.none")}</EmptyState>;
  return (
    <ul className="grid gap-3 sm:grid-cols-2" aria-label={t("connectors.providers.title")}>
      {cards.map((card) => <ProviderCardItem key={card.key} card={card} />)}
    </ul>
  );
}

function ProviderCardItem({ card }: { card: ProviderCard }) {
  const { t } = useTranslation();
  // The state says what is missing; only a provider that fails needs the connector's own words.
  const problem = card.pcs.find((pc) => pc.state === "not_working" && pc.problem !== null)?.problem ?? null;
  return (
    <li className="app-card flex flex-col gap-2 p-3" aria-label={cardTitle(t, card)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="app-title truncate text-sm font-semibold">{cardTitle(t, card)}</h3>
          <p className="app-text-muted text-xs">
            {/* Agents lead the list; only an API key says what it is (F78). */}
            {[card.kind === "api_key" ? t("connectors.providers.apiKey") : null, card.models > 0 ? t("connectors.providers.models", { count: card.models }) : null]
              .filter((part) => part !== null)
              .join(" · ")}
          </p>
        </div>
        {card.state === null ? null : <StateChip state={card.state} />}
      </div>
      {/* Its PCs differ (F86): each says its own state; no badge speaks for all of them. */}
      {card.state === null ? (
        <ul className="space-y-1 text-xs" aria-label={t("connectors.providers.perPc")}>
          {card.pcs.map((pc) => (
            <li key={pc.connectorId} className="flex items-center justify-between gap-2">
              <span className="app-text min-w-0 truncate">{pc.pcLabel}</span>
              <StateChip state={pc.state} />
            </li>
          ))}
        </ul>
      ) : null}
      {problem === null ? null : <p className="app-text-muted text-xs">{problem}</p>}
      <ConnectAction card={card} />
    </li>
  );
}

function StateChip({ state }: { state: ProviderState }) {
  const { t } = useTranslation();
  return <Chip tone={STATE_TONES[state]} className="shrink-0">{t(`connectors.providers.states.${state}`)}</Chip>;
}

/** An agent by its own name; an API route by its provider, and its host where the kind alone doesn't say (DR-C-08). */
function cardTitle(t: TFunction, card: ProviderCard): string {
  if (card.provider === null) return card.name;
  const { api, host } = card.provider;
  return api === null || api === "openai_compatible" ? host : t(`connectors.routes.apiKinds.${api}`);
}

/**
 * What repairs a provider on the PCs that need it (OW-05), each named. An online PC with its own
 * page gets a button that opens that page there, through the server (F85): its agents to sign
 * in or set up, its keys to add one. A PC without one, or a page that couldn't open, offers the
 * command, folded away (F47). An offline PC gets nothing here: its line above says to start its
 * connector (P-B).
 */
function ConnectAction({ card }: { card: ProviderCard }) {
  const { t, i18n } = useTranslation();
  const listOf = (pcs: readonly ProviderOnPc[]) => new Intl.ListFormat(i18n.resolvedLanguage, { type: "conjunction" }).format(pcs.map((onPc) => onPc.pcLabel));
  if (card.repairOn.length === 0) return null;
  const withPage = card.repairOn.filter((onPc) => onPc.hasPage);
  const withoutPage = card.repairOn.filter((onPc) => !onPc.hasPage);
  const labelOf = (onPc: ProviderOnPc) =>
    t(card.kind === "api_key" ? "connectors.providers.addKeyOn" : onPc.state === "needs_sign_in" ? "connectors.providers.signInOn" : "connectors.providers.setUpOn", { pc: onPc.pcLabel });
  return (
    <div className="mt-auto space-y-2">
      {withPage.map((onPc) => (
        <OpenOnPc
          key={onPc.connectorId}
          connectorId={onPc.connectorId}
          pc={onPc.pcLabel}
          section={card.kind === "api_key" ? "keys" : "agents"}
          signInAgent={card.kind !== "api_key" && onPc.state === "needs_sign_in" ? card.routeId : undefined}
          label={labelOf(onPc)}
          command={repairCommand(card, onPc)}
        />
      ))}
      {withoutPage.length === 0 ? null : (
        <CommandFold>
          <p>{t("connectors.providers.inTerminal", { pcs: listOf(withoutPage) })}</p>
          <ConnectorCommandLine command={repairCommand(card, withoutPage[0])} />
        </CommandFold>
      )}
    </div>
  );
}

/**
 * The terminal's way to the same repair: an agent's sign-in, or the PC's own page, which sets
 * agents up. For a key, `status` names the exact `keys set` command, since the key's name is
 * the PC's own; it works also where the page is off or too old for keys.
 */
function repairCommand(card: ProviderCard, onPc: ProviderOnPc | undefined): ConnectorCommand {
  if (card.kind === "api_key") return { args: "status", aboutServer: false };
  return onPc?.state === "needs_sign_in" ? { args: `agents login ${card.routeId}`, aboutServer: false } : { args: "tools page", aboutServer: true };
}
